/**
 * Opens the Truemed "Learn more" pop-up drawn by blocks/ora-truemed.liquid, on
 * the tab that matches how the shopper is buying.
 *
 * The two flows differ in a way that matters: a one-time order pays with
 * Truemed at checkout, a subscription must not be paid with an HSA/FSA card at
 * all and is reimbursed afterwards. Most orders default to Subscribe & Save, so
 * opening on the one-time steps would tell a subscriber to do the wrong thing.
 *
 * Everything is delegated from the document, so a block that is re-rendered by
 * a variant change -- or cloned into the quick-add sheet -- needs no setup.
 */

const ROOT = '.ora-truemed';

/**
 * Reads the purchase option chosen beside this block: Stay.ai's widget on a
 * product page, or `ora-purchase-options` on a partner page. Falls back to the
 * one-time steps, which is Truemed's own default.
 * @param {Element} root
 * @returns {'once' | 'sub'}
 */
function purchaseMode(root) {
  const scope =
    root.closest('.product-information, .shopify-section, dialog') ?? document;

  const own = scope.querySelector('ora-purchase-options[data-mode]');
  if (own instanceof HTMLElement) return own.dataset.mode === 'once' ? 'once' : 'sub';

  const checked = scope.querySelector('purchase-options input[name="purchaseType"]:checked');
  const label = checked?.closest('.stayai-purchase-label');
  if (label?.querySelector('[data-stayai-subscription-price]')) return 'sub';

  return 'once';
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {'once' | 'sub'} mode
 * @param {boolean} [focus]
 */
function selectTab(dialog, mode, focus = false) {
  for (const tab of dialog.querySelectorAll('[role="tab"]')) {
    const on = tab instanceof HTMLElement && tab.dataset.mode === mode;
    tab.setAttribute('aria-selected', String(on));
    tab.setAttribute('tabindex', on ? '0' : '-1');
    if (on && focus && tab instanceof HTMLElement) tab.focus();
  }
  for (const panel of dialog.querySelectorAll('[role="tabpanel"]')) {
    if (panel instanceof HTMLElement) panel.hidden = panel.dataset.mode !== mode;
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const opener = target.closest('[data-ora-truemed-open]');
  if (opener) {
    const root = opener.closest(ROOT);
    const dialog = root?.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement) || !root) return;
    event.preventDefault();
    selectTab(dialog, purchaseMode(root));
    dialog.showModal();
    return;
  }

  const dialog = target.closest('.ora-truemed__dialog');
  if (!(dialog instanceof HTMLDialogElement)) return;

  // A click on the dialog element itself is a click on the backdrop: the box
  // inside fills it, so anything else lands on a descendant.
  if (target === dialog || target.closest('[data-ora-truemed-close]')) {
    dialog.close();
    return;
  }

  const tab = target.closest('[role="tab"]');
  if (tab instanceof HTMLElement) selectTab(dialog, tab.dataset.mode === 'sub' ? 'sub' : 'once');
});

// Arrow keys move between the two tabs, as a tablist should.
document.addEventListener('keydown', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') return;
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

  const dialog = target.closest('.ora-truemed__dialog');
  if (!(dialog instanceof HTMLDialogElement)) return;

  event.preventDefault();
  selectTab(dialog, target.dataset.mode === 'sub' ? 'once' : 'sub', true);
});
